import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';

const Dashboard = ({ user, setAuth }) => {
  const [metrics, setMetrics] = useState(null);
  const [locations, setLocations] = useState([]); // Add state for locations
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize date state with today's date in YYYY-MM-DD format
  const today = new Date();
  const formattedToday = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  const [date, setDate] = useState(formattedToday);
  console.log('Initial date state:', formattedToday);
  const [selectedLocation, setSelectedLocation] = useState('');
  const navigate = useNavigate();
  const dashboardRef = useRef(null);

  // Use useCallback to memoize the handleLogout function
  const handleLogout = useCallback(() => {
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear axios default header
    delete axios.defaults.headers.common['Authorization'];
    
    // Update auth state
    setAuth({
      isAuthenticated: false,
      user: null,
      loading: false
    });
    
    // Redirect to login
    navigate('/login');
  }, [navigate, setAuth]);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Include the selectedLocation in the API request
        const response = await axios.get(`/api/metrics?date=${date}&location=${selectedLocation}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setMetrics(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching metrics:', err);
        setError('Failed to load metrics data. Please try again later.');
        
        // If unauthorized, redirect to login
        if (err.response && err.response.status === 401) {
          handleLogout();
        }
      } finally {
        setLoading(false);
      }
    };
    

    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/locations', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('Fetched locations:', response.data); // Log fetched locations
        setLocations(response.data);
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError('Failed to load locations. Please try again later.');
      }
    };

    fetchMetrics();
    fetchLocations(); // Fetch locations when component mounts
  }, [date, selectedLocation, navigate, handleLogout]);

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    console.log('Date selected from input:', selectedDate);
    // Ensure date is in YYYY-MM-DD format
    setDate(selectedDate);
  };

  // Handle export to PDF/CSV
  const handleExport = async (format) => {
    // Use server-side endpoint for both PDF and CSV exports
    const token = localStorage.getItem('token');
    
    // Ensure date is properly formatted as YYYY-MM-DD for the server
    // This is critical to ensure the correct date is used in the export
    const formattedDate = date;
    console.log('Date from state:', date);
    console.log('Formatted date for export:', formattedDate);
    
    const url = `/api/metrics/export/${format.toLowerCase()}?date=${formattedDate}&location=${selectedLocation}`;
    console.log(`Exporting ${format} with date: ${formattedDate} and location: ${selectedLocation}`);
    
    if (format.toLowerCase() === 'pdf') {
      try {
        setLoading(true);
        console.log(`Starting PDF export using server endpoint: ${url}`);
        
        // Make a direct fetch request to the server for PDF data
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        // Get the blob from the response
        const blob = await response.blob();
        
        // Create a download link and trigger it
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Include location in the filename if available
        const locationPart = selectedLocation ? `-${selectedLocation.replace(/\s+/g, '-')}` : '';
        link.setAttribute('download', `metrics-dashboard-${locationPart}-${date}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        window.URL.revokeObjectURL(downloadUrl);
        console.log('PDF downloaded successfully');
      } catch (err) {
        console.error('Error generating PDF:', err);
        // Log more details about the error
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        
        // Provide more specific error message based on the error type
        if (err.message && err.message.includes('autoTable')) {
          setError('Failed to generate PDF: Error with table formatting. Please try again.');
        } else if (err.message && err.message.includes('undefined')) {
          setError('Failed to generate PDF: Missing data. Please ensure metrics data is loaded.');
        } else if (err.message && err.message.includes('plugin not available')) {
          setError('Failed to generate PDF: Required plugin not available. Please refresh the page and try again.');
        } else {
          setError(`Failed to generate PDF: ${err.message || 'Unknown error'}. Please try again.`);
        }
      } finally {
        setLoading(false);
      }
    } else if (format.toLowerCase() === 'csv') {
      // Use server-side endpoint for CSV export
      const token = localStorage.getItem('token');
      const url = `/api/metrics/export/${format.toLowerCase()}?date=${date}&location=${selectedLocation}`; // Include selectedLocation
  
      try {
        // Make a direct fetch request to the server for CSV data
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        // Get the blob from the response
        const blob = await response.blob();
        
        // Create a download link and trigger it
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Include location in the filename if available
        const locationPart = selectedLocation ? `-${selectedLocation.replace(/\s+/g, '-')}` : '';
        link.setAttribute('download', `metrics-dashboard-${locationPart}-${date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        window.URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        console.error('Error exporting CSV:', err);
        setError(`Failed to export CSV: ${err.message}`);
      }
    }
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value); // Ensure this updates the state correctly
  };

  return (
    <div className="dashboard-container" ref={dashboardRef}>
      <header className="dashboard-header">
        <div>
          <h1>Metrics Dashboard</h1>
          {locations.length > 0 && (
            <div className="practice-info">
              {selectedLocation && locations.find(loc => loc.location_name === selectedLocation) ? (
                <>
                  <span>Practice: {locations.find(loc => loc.location_name === selectedLocation).practice_name} </span>
                  <span className="practice-id">(ID: {locations.find(loc => loc.location_name === selectedLocation).practice_id})</span>
                </>
              ) : (
                <>
                  <span>Practice: {locations[0].practice_name} </span>
                  <span className="practice-id">(ID: {locations[0].practice_id})</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="user-info">
          <span>Welcome, {user?.firstName || 'User'}</span>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </header>
      
      <div className="dashboard-content">
        <div className="date-selector">
          <label htmlFor="date-input">Select Date:</label>
          <input 
            type="date" 
            id="date-input" 
            value={date} 
            onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
          <label htmlFor="location-select" className="text-sm font-medium">All Locations:</label>
          <select
            id="location-select"
            value={selectedLocation}
            onChange={handleLocationChange}
            className="border border-gray-300 rounded p-2 text-sm"
          >
            <option value="">All Locations</option>
            {locations.map((location) => (
              <option key={location.location_name} value={location.location_name}>
                {location.location_name}
              </option>
            ))}
          </select>
          <div className="export-buttons">
            <button 
              onClick={() => handleExport('PDF')} 
              className="export-button"
              disabled={loading || !metrics}
            >
              Export PDF
            </button>
            <button 
              onClick={() => handleExport('CSV')} 
              className="export-button"
              disabled={loading || !metrics}
            >
              Export CSV
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading metrics data...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : metrics ? (
          <div className="metrics-container">
            <div className="metrics-summary">
              <div className="metric-card">
                <h3>Completed Calls</h3>
                <p className="metric-value">{metrics.totalCalls || 0}</p>
              </div>
              <div className="metric-card">
                <h3>Successful Reschedules</h3>
                <p className="metric-value">{metrics.successfulReschedules || 0}</p>
              </div>
              <div className="metric-card">
                <h3>Autonomous Completion Rate</h3>
                <p className="metric-value">{metrics.autonomousCompletionRate || 0}%</p>
              </div>
              <div className="metric-card">
                <h3>Agent Transfers</h3>
                <p className="metric-value">{metrics.agentTransfers || 0}</p>
              </div>
            </div>
            
                {/* Pending Review Transactions */}
                {/* Make sure pendingReview section appears after transactions */}
                {metrics.pendingReview && metrics.pendingReview.length > 0 && (
                  <div className="transactions-section">
                    <h3>Pending Review Transactions</h3>
                    <table className="transactions-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>PID</th>
                          {/* <th>Appointment ID</th> */}
                          <th>Appt. Type</th>
                          <th>Location</th>
                          <th>Original Appt</th>
                          <th>Appt. Status</th>
                          <th>Call Status</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.pendingReview.map((transaction, index) => (
                          <tr key={index}>
                            <td>{transaction.time}</td>
                            <td>{transaction.patientId}</td>
                            {/* <td>{transaction.appt_external_id || 'N/A'}</td> */}
                            <td>{transaction.appointmentType}</td>
                            <td>{transaction.location}</td>
                            <td>{transaction.originalDateTime_formatted || transaction.original_datetime || transaction.originalDateTime || 'N/A'}</td>
                            <td>{transaction.status}</td>
                            <td>{transaction.call_status}</td>
                            <td>{transaction.reason || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Todays Incomplete Calls Section */}
                {metrics.todays_incomplete_calls && metrics.todays_incomplete_calls.length > 0 && (
                  <div className="transactions-section">
                    <h3 style={{ textAlign: 'center', width: '100%' }}>Today's Incomplete Calls</h3>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                    {(() => {
                      const calls = metrics.todays_incomplete_calls;
                      const half = Math.ceil(calls.length / 2);
                      const firstHalf = calls.slice(0, half);
                      const secondHalf = calls.slice(half);
                      return (
                        <>
                          <table className="transactions-table incomplete-calls-table" style={{ width: '50%'}}>
                            <thead>
                              <tr style={{ display: 'flex', textAlign: 'left'}}>
                                <th style={{ width: '25%', paddingLeft: '12px' }}>Time</th>
                                <th style={{ width: '50%', paddingLeft: '12px' }}>Caller No.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {firstHalf.map((call, idx) => (
                                <tr key={idx} style={{ display: 'flex', justifyContent: 'left' }}>
                                  <td style={{ width: '25%' }}>{call.time || call.timestamp || 'N/A'}</td>
                                  <td style={{ width: '50%' }}>{call.from_number || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <table className="transactions-table incomplete-calls-table" style={{ width: '50%' }}>
                            <thead>
                              <tr style={{ display: 'flex', textAlign: 'left' }}>
                                <th style={{ width: '25%', paddingLeft: '12px' }}>Time</th>
                                <th style={{ width: '50%', paddingLeft: '12px' }}>Caller No.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {secondHalf.map((call, idx) => (
                                <tr key={idx} style={{ display: 'flex' }}>
                                  <td style={{ width: '25%' }}>{call.time || call.timestamp || 'N/A'}</td>
                                  <td style={{ width: '50%' }}>{call.from_number || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      );
                    })()}
                    </div>
                  </div>
                )}
            {metrics.transactions && metrics.transactions.length > 0 && (
              <div className="transactions-section">
                <h3>Recent Transactions</h3>
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                        <th>PID</th>
                        {/* <th>call_ID</th> */}
                          {/* <th>Appointment ID</th> */}
                      <th>Appointment Type</th>
                      <th>Location</th>
                      <th>Original Appt</th>
                      <th>New Appt</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.transactions.map((transaction, index) => (
                      <tr key={index}>
                        <td>{transaction.time}</td>
                        <td>{transaction.patientId}</td>
                        {/* <td>{transaction.call_id}</td> */}
                        {/* <td>{transaction.appt_external_id || 'N/A'}</td> */}
                        <td>{transaction.appointmentType}</td>
                        <td>{transaction.location}</td>
                        <td>{transaction.originalDateTime_formatted || transaction.original_datetime || transaction.originalDateTime || 'N/A'}</td>
                        <td>{transaction.newDateTime_formatted || 'N/A'}</td>
                        <td>{transaction.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            
          </div>
        ) : (
          <div className="no-data">No metrics data available for the selected date.</div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;